import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PhoneCall, Headphones, ClipboardList, Rocket, PackageCheck } from "lucide-react";
import TID from "@/lib/testIds";

const steps = [
  { n: 1, title: "Contact Us", desc: "Tell us about your project. We respond within hours.", icon: PhoneCall },
  { n: 2, title: "Free Consultation", desc: "Scoping call to align on goals, timelines and budget.", icon: Headphones },
  { n: 3, title: "Planning", desc: "We craft a detailed roadmap, success metrics and milestones.", icon: ClipboardList },
  { n: 4, title: "Execution", desc: "Senior engineers and growth experts execute with weekly updates.", icon: Rocket },
  { n: 5, title: "Delivery", desc: "Polished delivery, training, and ongoing support.", icon: PackageCheck },
];

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 30%"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      data-testid={TID.processSection}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-[0.18em]">Our Process</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            From first call to <span className="text-gradient-brand">flawless delivery</span>
          </h2>
        </div>

        <div ref={ref} className="relative mt-16 max-w-3xl mx-auto">
          {/* Track */}
          <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-slate-200 dark:bg-white/10" aria-hidden />
          {/* Progress line */}
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 w-px bg-gradient-to-b from-brand-blue to-brand-purple"
            aria-hidden
          />

          <div className="space-y-12 md:space-y-16">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const right = i % 2 === 1;
              return (
                <motion.div
                  key={s.n}
                  data-testid={TID.processStep(s.n)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5 }}
                  className={`relative md:grid md:grid-cols-2 md:gap-12 ${
                    right ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className={`pl-16 md:pl-0 ${right ? "md:text-left md:pl-12" : "md:text-right md:pr-12"}`}>
                    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Step {s.n}
                    </span>
                    <h3 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-slate-600 dark:text-slate-300 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>

                  {/* Marker */}
                  <span
                    className="absolute left-6 md:left-1/2 -translate-x-1/2 top-0 w-12 h-12 rounded-2xl bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-white/10 shadow-elevated inline-flex items-center justify-center"
                    aria-hidden
                  >
                    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-blue to-brand-purple text-white inline-flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5" />
                    </span>
                  </span>

                  <div />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
